"use client";

import { useEffect, useRef } from "react";
import {
	PROGRAMA_INDICACAO_EMBED_PARENT_ORIGINS,
	PROGRAMA_INDICACAO_IFRAME_MESSAGE,
	type ProgramaIndicacaoEmbedSource,
	type ProgramaIndicacaoResizeMessage,
} from "@/app/config/programaIndicacaoEmbed";

const HEIGHT_PADDING_PX = 8;

function isEmbedded(): boolean {
	if (typeof window === "undefined") return false;
	try {
		return window.parent !== window;
	} catch {
		return true;
	}
}

function resolveParentOrigin(): string | null {
	if (typeof document === "undefined" || !document.referrer) return null;
	try {
		return new URL(document.referrer).origin;
	} catch {
		return null;
	}
}

function getTargetOrigins(): string[] {
	const parentOrigin = resolveParentOrigin();
	// A origem do pai vem do referrer; o CSP frame-ancestors já restringe quem pode embedar.
	if (parentOrigin) return [parentOrigin];
	return [...PROGRAMA_INDICACAO_EMBED_PARENT_ORIGINS];
}

function measureHeight(root: HTMLElement | null): number {
	if (!root) return 0;
	// Mede só o conteúdo real; body/html herdam a altura do iframe e causam loop infinito.
	return Math.ceil(root.scrollHeight);
}

export function useIframeHeightPostMessage(source: ProgramaIndicacaoEmbedSource) {
	const rootRef = useRef<HTMLDivElement>(null);
	const lastHeightRef = useRef(0);
	const rafRef = useRef<number | null>(null);

	useEffect(() => {
		if (!isEmbedded()) return;

		const targets = getTargetOrigins();

		const notify = () => {
			const root = rootRef.current;
			if (!root) return;

			if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);

			rafRef.current = requestAnimationFrame(() => {
				rafRef.current = null;
				const height = measureHeight(root) + HEIGHT_PADDING_PX;
				if (height === lastHeightRef.current) return;
				lastHeightRef.current = height;

				const payload: ProgramaIndicacaoResizeMessage = {
					type: PROGRAMA_INDICACAO_IFRAME_MESSAGE.RESIZE,
					height,
					source,
				};

				for (const origin of targets) {
					window.parent.postMessage(payload, origin);
				}
			});
		};

		const root = rootRef.current;
		if (!root) return;

		notify();

		const resizeObserver = new ResizeObserver(notify);
		resizeObserver.observe(root);

		const mutationObserver = new MutationObserver(notify);
		mutationObserver.observe(root, {
			childList: true,
			subtree: true,
			attributes: true,
		});

		const onImageLoad = (event: Event) => {
			if (event.target instanceof HTMLImageElement && root.contains(event.target)) {
				notify();
			}
		};

		document.addEventListener("load", onImageLoad, true);

		return () => {
			if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
			resizeObserver.disconnect();
			mutationObserver.disconnect();
			document.removeEventListener("load", onImageLoad, true);
		};
	}, [source]);

	return rootRef;
}
